/*jshint browserify: true */
'use strict';

var PdfElement = require('./pdf-element.js');

/**
 * @name PdfTextElement
 *
 * A text element will render some text on-top of a PDF page.
 */
function PdfTextElement() {
    PdfElement.call(this);

    this.placeHolder = '';
    this.characterCount = 0;
    this.characterSpacings = [];
    this.fontSize = 10;
    this.fontName = 'Arial';
    this.fontColour = '#000000';
    this.fontBold = false;
    this.fontItalic = false;
}

PdfTextElement.prototype = Object.create(PdfElement.prototype);
PdfTextElement.prototype.constructor = PdfTextElement;

function drawCircle(context, x, y, radius) {
    context.beginPath();
    context.arc(x, y, radius, 0, 2 * Math.PI);
    context.fill();
}

/**
 * @name autoSpaceCharacters
 * @property {bool}
 */
Object.defineProperty(PdfTextElement.prototype, 'autoSpaceCharacters', {
    get: function () {
        return this.characterSpacings.length === 0;
    },
    set: function (newVal) {
        if (newVal) {
            this.characterSpacings.length = 0;
        } else if (this.characterCount > 0) {
            this.setDefaultCharacterSpacings();
        }
    }
});

/**
 * @name setDefaultCharacterSpacings
 *
 * When in auto-spacing mode, the array of character positions is empty. This function populates the character
 * positions array with values that match the auto-spacing.
 */
PdfTextElement.prototype.setDefaultCharacterSpacings = function () {
    var widthPerChar = this.width / this.characterCount;

    this.characterSpacings.push(widthPerChar / 2);
    for (var j = 1; j < this.characterCount; j++) {
        this.characterSpacings.push(widthPerChar);
    }
};

/**
 * @name calculateCharacterPositions
 * @description Calculate character positions in viewport-space, useful for drawing and hit-test
 *
 * @returns {Array} Array of objects describing character placeholder positions:
 *   x: x coordinate for centre of placeholder
 *   y: y coordinate for centre of placeholder
 *   radius: pixel radius for placeholder circle
 */
PdfTextElement.prototype.calculateCharacterPositions = function (pdfViewport) {
    var characterPositions = [];
    var translatedRectangle = coordinates.translatePdfRectangleToViewportRectangle(pdfViewport, this);
    var scaledFontSize = this.fontSize * pdfViewport.scale;
    var i;

    if (this.characterCount > 0 && this.characterSpacings.length === 0) {
        // Automatically position each character

        var widthPerChar = translatedRectangle.width / this.characterCount;

        for (i = 0; i < this.characterCount; i++) {
            characterPositions.push({
                x: translatedRectangle.x + (widthPerChar / 2) + (i * widthPerChar),
                y: translatedRectangle.y + (translatedRectangle.height / 2),
                radius: scaledFontSize / 4
            });
        }
    } else if (this.characterCount > 0 && this.characterSpacings.length > 0) {
        // Position each character according to specified spacings

        var currentSpacing = 0;
        for (i = 0; i < this.characterSpacings.length; i++) {
            currentSpacing += (this.characterSpacings[i] * pdfViewport.scale);

            characterPositions.push({
                x: translatedRectangle.x + currentSpacing,
                y: translatedRectangle.y + (translatedRectangle.height / 2),
                radius: scaledFontSize / 4
            });
        }
    }

    return characterPositions;
};

PdfTextElement.prototype.renderToCanvas = function (pdfViewport, canvasContext, selected, mousePosition, interactionMode) {
    var permitHoverStyle = true;
    if (interactionMode instanceof SelectionMode) {
        if (interactionMode._subMode === SelectionMode.subModes.RESIZE ||
            interactionMode._subMode === SelectionMode.subModes.TEXT_SPACING ||
            interactionMode._subMode === SelectionMode.subModes.MOVE) {

            permitHoverStyle = false;
        }
    }

    var elementRectangle = new coordinates.Rectangle(this.x, this.y, this.width, this.height, pdfViewport);

    //
    // Fill
    //

    canvasContext.save();

    if (permitHoverStyle && elementRectangle.positionIsInsideRectangle(mousePosition)) {
        canvasContext.fillStyle = 'blue';
        canvasContext.globalAlpha = 0.3;
    } else {
        canvasContext.fillStyle = PdfElement.colourSettings.fillColour;
        canvasContext.globalAlpha = PdfElement.colourSettings.fillAlpha;
    }
    canvasContext.fillRect(elementRectangle.viewportLeft, elementRectangle.viewportTop, elementRectangle.viewportWidth, elementRectangle.viewportHeight);

    canvasContext.restore();


    //
    // Baselines for multi-line textbox
    //

    if (this.characterCount === 0) {
        multiLineTextboxRender(this.fontSize, elementRectangle, pdfViewport.scale, canvasContext);
    }


    //
    // Border
    //

    canvasContext.save();

    if (selected) {
        canvasContext.lineWidth = PdfElement.colourSettings.borderWidthSelected;
        canvasContext.strokeStyle = PdfElement.colourSettings.borderColourSelected;
        canvasContext.globalAlpha = PdfElement.colourSettings.borderAlphaSelected;
        canvasContext.strokeRect(elementRectangle.viewportLeft, elementRectangle.viewportTop, elementRectangle.viewportWidth, elementRectangle.viewportHeight);
    } else {
        canvasContext.lineWidth = PdfElement.colourSettings.borderWidth;
        canvasContext.strokeStyle = PdfElement.colourSettings.borderColour;
        canvasContext.globalAlpha = PdfElement.colourSettings.borderAlpha;
        canvasContext.strokeRect(elementRectangle.viewportLeft, elementRectangle.viewportTop, elementRectangle.viewportWidth, elementRectangle.viewportHeight);
    }

    canvasContext.restore();


    //
    // Character Place-holder Dots
    //

    if (this.characterCount > 0) {
        var characterPositions = this.calculateCharacterPositions(pdfViewport);

        var indexOfCharacterBeingMoved = -1;
        if (interactionMode instanceof SelectionMode && interactionMode._subMode === SelectionMode.subModes.TEXT_SPACING &&
            interactionMode._subModeObject._textElement === this) {

            indexOfCharacterBeingMoved = interactionMode._subModeObject._characterIndex;
        }

        canvasContext.save();

        canvasContext.fillAlpha = 1;
        for (var i = 0; i < characterPositions.length; i++) {
            if (i === indexOfCharacterBeingMoved || (indexOfCharacterBeingMoved === -1 && mouseIsInCircle(characterPositions[i], mousePosition))) {
                canvasContext.fillStyle = 'red';
            } else {
                canvasContext.fillStyle = 'black';
            }
            drawCircle(canvasContext, characterPositions[i].x, characterPositions[i].y, characterPositions[i].radius);
        }

        canvasContext.restore();
    }
};

/**
 * @param {PDFedit.document.Page} page
 * @param {MouseEvent} mouseEvent
 *
 * @return {number} index of the character that the mouse position is over, or -1 if it is not over a character
 */
PdfTextElement.prototype.mousePositionCharacterHit = function (page, mouseEvent) {
    var characterPositions = this.calculateCharacterPositions(page.pageViewport);
    var mousePosition = coordinates.Position.fromMouseEvent(mouseEvent, page.pageViewport, page.pdfOverlayCanvasElement);

    for (var i = 0; i < characterPositions.length; i++) {
        if (mouseIsInCircle(characterPositions[i], mousePosition)) {
            return i;
        }
    }

    return -1;
};

/**
 * @param {Object} circlePosition - Position in viewport coordinates, including properties for x, y and radius.
 * @param {PDFedit.coordinates.Position} mousePosition
 */
function mouseIsInCircle(circlePosition, mousePosition) {
    // Just treat the circle as though it's a square
    return mousePosition.viewportX >= circlePosition.x - circlePosition.radius &&
        mousePosition.viewportX <= circlePosition.x + circlePosition.radius &&
        mousePosition.viewportY >= circlePosition.y - circlePosition.radius &&
        mousePosition.viewportY <= circlePosition.y + circlePosition.radius;
}

module.exports = PdfTextElement;
    
var SelectionMode = require('../UImodes/SelectionMode.js');
var multiLineTextboxRender = require('../pdf-multi-line-textbox-render.js');
var coordinates = require('../Coordinates');